import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { grey, lightBlue } from '@mui/material/colors'
import { useFlags } from 'launchdarkly-react-client-sdk'
import noop from 'lodash/noop'
import upperCase from 'lodash/upperCase'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, shallowEqual, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import api from 'api'
import { CACHE_TAG_CONFIGURED_SERVICE_DETAILS, CACHE_TAG_CONFIGURED_SERVICES_LIST } from 'api/cacheTagTypes'
import { TabPanel } from 'components/common'
import { METHOD_TO_ICON_MAP } from 'components/common/chip/MethodChip'
import EventBlocker from 'components/common/EventBlocker'
import HHDisplayMoneyBase from 'components/common/HHDisplayMoney'
import ActivityProvider from 'components/configured-service/accordions/ActivityAccordion/ActivityProvider'
import ConfiguredServiceActivityAccordion from 'components/configured-service/accordions/ActivityAccordion/ConfiguredServiceActivityAccordion'
import ActionBasedPricingIcon from 'components/configured-service/common/ActionBasedPricingIcon'
import ContainersChip from 'components/configured-service/common/ContainersChip'
import {
  AdjustServiceProvider as AdjustServiceProviderBase,
  useAdjustService,
} from 'components/configured-service/layout/AdjustServiceContext'
import AdjustServicePanel from 'components/configured-service/layout/AdjustServicePanel'
import ServiceDateChips from 'components/configured-service/layout/ServiceDateChips'
import { useCSDetailsCachedData } from 'components/configured-service/useCSDetailsCachedData'
import ConfiguredServiceActionMenu from 'components/customer-details/tables/ConfiguredServices/ConfiguredServiceActionMenu'
import { getServiceRecurrenceText } from 'components/locations/LocationsDataGrid/RenderLocationDetailPanelContent/ConfiguredServiceCard'
import GracePeriodBadgeButton from 'components/pricing/RentalFees/common/badge/GracePeriodBadgeButton'
import RentalFeeBadgeButton from 'components/pricing/RentalFees/common/badge/RentalFeeBadgeButton'
import InteractiveTagsList from 'components/tags/InteractiveTagsList'
import MeasureChip from 'containers/new-route-manager/RouteCard/MeasureChip'
import { selectBillingName } from 'data/configured-service/configuredServiceDetailsSelectors'
import { isRentalFeeFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { selectIsInventoryFeatureEnabled } from 'data/tenant/tenantSettingsSelectors'
import { getConfiguredServiceDetailsPageUrl } from 'router/routes'
import T from 'T'
import { BACKGROUND } from 'theme/colors'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { get } from 'utils/lodash'
import { memo } from 'utils/react'
import { getPricingPeriod } from 'utils/service'

import { StyledAccordion } from '../work-orders/layout/StyledAccordion'
import WorkOrderLayoutBase from '../work-orders/layout/WorkOrderLayout'

import CSNotesBadge from './actions/CSNotesBadge'
import ServiceContent from './actions/service-details/ServiceContent'
import ServiceDetails from './actions/service-details/ServiceDetails'
import WeeklySummaryBase from './actions/weekly-summary/WeeklySummary'
import { CONFIGURED_SERVICE_STATUS, CONFIGURED_SERVICES_EXPANDED_TABS } from './settings'

import type { Tag } from 'types/entities/shared'

const { DEACTIVATED } = CONFIGURED_SERVICE_STATUS
const { SERVICE_DETAILS, WORK_ORDERS, ACTIVITY_HISTORY, EDIT } = CONFIGURED_SERVICES_EXPANDED_TABS

type ConfiguredServiceListItem = {
  configuredServiceId?: string
  accountId?: string
  serviceName?: string
  serviceType?: string
  servicePrice?: number
  pricingPeriodInterval?: string
  pricingPeriodPer?: string
  activeNoteCount?: number
  rentalFeeCount?: number
  rentalFeeResetCount?: number
  status?: string
  tags?: Tag[]
  methodType?: string
  measureUnit?: string
  measureVolume?: number
  workOrders?: unknown[]
  actionPriceCount?: number
  quantity?: number
  newContainerCount?: number
}

type ServiceAccordionItemProps = {
  service?: ConfiguredServiceListItem
  selectedServiceId?: string
  canEdit?: boolean
  showAddEventButton?: boolean
  onAddEventClick?: (event: React.SyntheticEvent, service: ConfiguredServiceListItem) => void
  locationId?: string
  onClick?: (event: React.MouseEvent) => void
}

// Untyped/out-of-scope .jsx children — cast at the module boundary so this .tsx can render them.
const HHDisplayMoney = HHDisplayMoneyBase as React.ElementType
const WeeklySummary = WeeklySummaryBase as React.ElementType
const WorkOrderLayout = WorkOrderLayoutBase as React.ElementType
const AdjustServiceProvider = AdjustServiceProviderBase as React.ElementType

const ServiceAccordionItemContent = ({
  service = {},
  selectedServiceId = '',
  canEdit = false,
  showAddEventButton = false,
  onAddEventClick = noop,
  locationId = '',
  onClick = noop,
}: ServiceAccordionItemProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const flags = useFlags()
  const isInventoryFeatureEnabled = useSelector(selectIsInventoryFeatureEnabled, shallowEqual)
  const accountId = get(service, 'accountId')
  const isRentalFeeEnabled = isRentalFeeFeatureAvailable({ flags })
  const [actionMenuAnchorEL, setActionMenuAnchorEL] = useState<HTMLElement | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedTab, setSelectedTab] = useState(SERVICE_DETAILS)

  const {
    isOpen: isAdjustServiceOpen,
    handleOpen: handleAdjustServiceOpen,
    handleClose: handleAdjustServiceClose,
    isDraft: isAdjustDraft,
    isUpdating: isAdjustUpdating,
    isPublishing: isAdjustPublishing,
    hasUnsavedChanges,
    onSaveClick,
    onSaveAndPublishClick,
  } = useAdjustService() as {
    isOpen: boolean
    handleOpen: () => void
    handleClose: () => void
    isDraft: boolean
    isUpdating: boolean
    isPublishing: boolean
    hasUnsavedChanges: boolean
    onSaveClick: () => void
    onSaveAndPublishClick: () => void
  }

  const {
    configuredServiceId,
    serviceName,
    serviceType,
    servicePrice = 0,
    pricingPeriodInterval = '',
    pricingPeriodPer,
    activeNoteCount = 0,
    rentalFeeCount = 0,
    rentalFeeResetCount = 0,
    status,
    tags = [],
    methodType = '',
    measureUnit = '',
    measureVolume = 0,
    workOrders = [],
    actionPriceCount = 0,
    quantity = 0,
    newContainerCount = 0,
  } = service

  // Billing name (when set) shows as a subtitle under the service name — mirrors the detail header.
  const { data: csDetailsData = {} } = useCSDetailsCachedData({ id: configuredServiceId })
  const billingName = useMemo(() => selectBillingName(csDetailsData), [csDetailsData])

  const containerCount = isInventoryFeatureEnabled ? newContainerCount : quantity
  const recurrenceLabel = useMemo(() => getServiceRecurrenceText(service), [service])
  const pricingPeriodText = pricingPeriodInterval === 'Instance' ? '' : getPricingPeriod(pricingPeriodPer, pricingPeriodInterval)
  const isRowSelected = useMemo(() => selectedServiceId === configuredServiceId, [selectedServiceId, configuredServiceId])
  const measureLabel = `${measureVolume} ${getMeasureUnit(measureUnit)}`
  const MethodIcon = get(METHOD_TO_ICON_MAP, upperCase(methodType), null)
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))
  const isExtraLarge = useMediaQuery(theme.breakpoints.up('xl'))

  const tagLimit = useMemo(() => {
    if (isExtraLarge) return 3
    if (isLarge) return 2
    return 1
  }, [isExtraLarge, isLarge])

  const rowBGColor = useMemo(() => {
    if (isRowSelected) return `${lightBlue[700]}14`
    if (status === DEACTIVATED) return BACKGROUND.deactivated
    return theme.palette.background.paper
  }, [isRowSelected, status])

  const toggleIsExpanded = (event: React.MouseEvent) => {
    event.stopPropagation()
    setIsExpanded(prev => !prev)
  }

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setActionMenuAnchorEL(event.currentTarget)
  }

  const handleCloseActionMenu = () => setActionMenuAnchorEL(null)

  const handleRowClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    const baseUrl = getConfiguredServiceDetailsPageUrl(configuredServiceId ?? '')
    const params = new URLSearchParams({ locationId, accountId }).toString()
    navigate(`${baseUrl}?${params}`)
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    if (newValue === EDIT) {
      handleAdjustServiceOpen()
    }
    setSelectedTab(newValue)
  }

  const handleCancelEdit = () => {
    handleAdjustServiceClose()
    setSelectedTab(SERVICE_DETAILS)
  }

  const handleEditService = () => {
    setIsExpanded(true)
    setSelectedTab(EDIT)
    handleAdjustServiceOpen()
  }

  const onSaveTags = (_data: unknown, closeCallback: () => void) => {
    closeCallback()
    dispatch(
      api.util.invalidateTags([CACHE_TAG_CONFIGURED_SERVICES_LIST, { type: CACHE_TAG_CONFIGURED_SERVICE_DETAILS, id: configuredServiceId }])
    )
  }

  const handleAddEvent = (e: React.MouseEvent) => {
    onAddEventClick(e, service)
  }

  useEffect(() => {
    if (!isAdjustServiceOpen && selectedTab === EDIT) {
      setSelectedTab(SERVICE_DETAILS)
    }
  }, [isAdjustServiceOpen])

  return (
    <>
      <StyledAccordion
        disablePointer
        elevation={0}
        expanded={isExpanded}
        expandIcon={undefined}
        iconGap={false}
        slotProps={{ transition: { unmountOnExit: true } }}
      >
        <AccordionSummary
          sx={{ p: 0, backgroundColor: rowBGColor, '&.Mui-focusVisible': { background: 'inherit' } }}
          onClick={canEdit ? onClick : undefined}
        >
          <Box alignItems="center" display="flex" gap={1} px={2} py={1} width="100%">
            <IconButton onClick={toggleIsExpanded}>{isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}</IconButton>

            <Box
              display="flex"
              flex={1}
              flexDirection="column"
              gap={0.5}
              sx={{ cursor: 'pointer', overflow: 'hidden', minWidth: 0 }}
              onClick={handleRowClick}
            >
              <Box alignItems="center" display="flex" gap={1} justifyContent="space-between" sx={{ padding: '6px 14px 0 0' }}>
                <Box alignItems="center" display="flex" gap={1}>
                  {MethodIcon && <MethodIcon color={theme.palette.text.secondary} height={24} style={{ minWidth: 24 }} width={24} />}
                  <Typography fontWeight={600} variant="h4">
                    {serviceName}
                  </Typography>
                  {billingName && <Divider flexItem orientation="vertical" />}
                  {billingName && (
                    <Typography noWrap sx={{ color: 'text.secondary', maxWidth: 240 }} variant="subtitle2">
                      {billingName}
                    </Typography>
                  )}
                  <Divider flexItem orientation="vertical" />
                  <Typography alignItems="center" display="flex" flexWrap="wrap" fontWeight={600} gap={0.5} variant="h4">
                    <HHDisplayMoney formatToDollars={false} value={servicePrice} />
                    {pricingPeriodText && <Typography variant="subtitle2">{`/ ${pricingPeriodText}`}</Typography>}
                  </Typography>
                  <ActionBasedPricingIcon configuredServiceId={configuredServiceId} count={actionPriceCount} />
                </Box>
                <Box alignItems="center" display="flex" gap={0.5}>
                  <EventBlocker>
                    <CSNotesBadge configuredServiceId={configuredServiceId} notesCount={activeNoteCount} />
                  </EventBlocker>
                  {isRentalFeeEnabled && (
                    <>
                      <EventBlocker>
                        <RentalFeeBadgeButton canEdit useV2 configuredServiceId={configuredServiceId} rentalFeeCount={rentalFeeCount} />
                      </EventBlocker>
                      <EventBlocker>
                        <GracePeriodBadgeButton
                          showTooltip
                          configuredServiceId={configuredServiceId}
                          rentalFeeResetCount={rentalFeeResetCount}
                        />
                      </EventBlocker>
                    </>
                  )}
                </Box>
              </Box>

              <Box
                display="flex"
                gap={1}
                justifyContent="space-between"
                sx={{
                  flexDirection: { xs: 'column', lg: 'row' },
                  alignItems: { xs: 'flex-start', lg: 'center' },
                  marginTop: { xs: 1.5, lg: 0.5 },
                }}
              >
                <Box alignItems="center" display="flex" gap={1}>
                  <ServiceDetails pricedServiceType={serviceType} serviceId={configuredServiceId} />
                  <Typography noWrap variant="body2">
                    {recurrenceLabel}
                  </Typography>
                  <ContainersChip label={`Quantity | ${containerCount}`} size="small" variant="outlined" />
                  {measureUnit && <MeasureChip label={measureLabel} measureUnit={measureUnit} />}
                  <WeeklySummary serviceType={serviceType} workOrders={workOrders} />
                </Box>
                <Box alignItems="center" display="flex" gap={0.5}>
                  <EventBlocker>
                    <ServiceDateChips serviceId={configuredServiceId} />
                  </EventBlocker>
                  <EventBlocker>
                    <InteractiveTagsList
                      disableAccount
                      disableLocation
                      accountId={accountId}
                      data={{ ...service, name: serviceName, configuredServiceId, tagDetails: { forService: tags } }}
                      GridContainerProps={{ direction: 'row', justifyContent: 'flex-end', alignItems: 'center', spacing: 0.5 }}
                      limit={tagLimit}
                      onSave={onSaveTags}
                    />
                  </EventBlocker>
                </Box>
              </Box>
            </Box>

            <Box alignItems="center" display="flex" justifyContent="center">
              {showAddEventButton && (
                <IconButton disabled={status === DEACTIVATED} size="small" onClick={handleAddEvent}>
                  <AddIcon />
                </IconButton>
              )}
              <IconButton size="small" onClick={handleOpenActionMenu}>
                <MoreVertIcon />
              </IconButton>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <Divider />
          <Box pl={2}>
            <Tabs sx={{ button: { textTransform: 'capitalize' } }} value={selectedTab} onChange={handleTabChange}>
              <Tab label={SERVICE_DETAILS} value={SERVICE_DETAILS} />
              <Tab label={WORK_ORDERS} value={WORK_ORDERS} />
              <Tab label={ACTIVITY_HISTORY} value={ACTIVITY_HISTORY} />
              {canEdit && <Tab label={EDIT} value={EDIT} />}
            </Tabs>
          </Box>
          <TabPanel index={SERVICE_DETAILS} value={selectedTab}>
            <ServiceContent bgcolor={grey[50]} isOpen={isExpanded} pl={2} serviceId={configuredServiceId} />
          </TabPanel>
          <TabPanel index={WORK_ORDERS} value={selectedTab}>
            {selectedTab === WORK_ORDERS && (
              <WorkOrderLayout
                accountId={accountId}
                configuredServiceId={configuredServiceId}
                noWOText="No work order for this service yet"
              />
            )}
          </TabPanel>
          <TabPanel index={ACTIVITY_HISTORY} value={selectedTab}>
            {selectedTab === ACTIVITY_HISTORY && (
              <ActivityProvider serviceId={configuredServiceId}>
                <ConfiguredServiceActivityAccordion hideHeader serviceId={configuredServiceId} />
              </ActivityProvider>
            )}
          </TabPanel>
          {canEdit && (
            <TabPanel index={EDIT} value={selectedTab}>
              <AdjustServicePanel serviceId={configuredServiceId ?? ''} />
              <Stack borderColor="divider" borderTop={1} direction="row" gap={1} justifyContent="flex-end" px={2} py={1}>
                <Button color="inherit" size="small" sx={{ textTransform: 'none' }} variant="text" onClick={handleCancelEdit}>
                  {T.CANCEL}
                </Button>
                <Tooltip
                  arrow
                  disableInteractive
                  disableHoverListener={hasUnsavedChanges}
                  title={!hasUnsavedChanges ? T.NO_CHANGES_MADE : ''}
                >
                  <span>
                    <Button
                      color="primary"
                      disabled={isAdjustUpdating || isAdjustPublishing || !hasUnsavedChanges}
                      loading={isAdjustUpdating}
                      size="small"
                      sx={{ textTransform: 'none' }}
                      variant="contained"
                      onClick={onSaveClick}
                    >
                      {T.SAVE}
                    </Button>
                  </span>
                </Tooltip>
                {isAdjustDraft && (
                  <Button
                    color="primary"
                    disabled={isAdjustUpdating || isAdjustPublishing}
                    loading={isAdjustPublishing}
                    size="small"
                    sx={{ textTransform: 'none' }}
                    variant="contained"
                    onClick={onSaveAndPublishClick}
                  >
                    {T.SAVE_AND_PUBLISH}
                  </Button>
                )}
              </Stack>
            </TabPanel>
          )}
        </AccordionDetails>
      </StyledAccordion>

      <ConfiguredServiceActionMenu
        accountId={accountId}
        anchorEl={actionMenuAnchorEL}
        canEdit={canEdit}
        locationId={locationId}
        selectedService={service}
        useRentalFeesV2={isRentalFeeEnabled}
        onClose={handleCloseActionMenu}
        onEditService={handleEditService}
      />
    </>
  )
}

const ServiceAccordionItem = ({
  service = {},
  selectedServiceId = '',
  canEdit = false,
  showAddEventButton = false,
  onAddEventClick = noop,
  locationId = '',
  onClick = noop,
}: ServiceAccordionItemProps) => {
  const configuredServiceId = get(service, 'configuredServiceId')
  const accountId = get(service, 'accountId')

  return (
    <AdjustServiceProvider accountId={accountId} locationId={locationId} serviceId={configuredServiceId}>
      <ServiceAccordionItemContent
        canEdit={canEdit}
        locationId={locationId}
        selectedServiceId={selectedServiceId}
        service={service}
        showAddEventButton={showAddEventButton}
        onAddEventClick={onAddEventClick}
        onClick={onClick}
      />
    </AdjustServiceProvider>
  )
}

export default memo(ServiceAccordionItem)
